import { Link } from "wouter";
import {
  BarChart3, FileText, Users, Shield, Globe, ChevronRight,
  ArrowRight, Layers, DollarSign, ClipboardList,
  Zap, Menu, X, PlayCircle, FolderOpen, LayoutDashboard,
  Briefcase, Wallet, TrendingUp,
  Building2, CheckCircle2, AlertTriangle, Clock, RefreshCw,
  Target, LineChart, Coins, BadgeCheck, Send,
} from "lucide-react";
import { useState, useEffect, useCallback } from "react";

/* ─── Country / dial-code data ───────────────────────────── */
const COUNTRIES: { code: string; name: string; flag: string; dial: string; example: string }[] = [
  { code: "AE", name: "United Arab Emirates", flag: "🇦🇪", dial: "+971", example: "50 123 4567" },
  { code: "SA", name: "Saudi Arabia",         flag: "🇸🇦", dial: "+966", example: "51 234 5678" },
  { code: "QA", name: "Qatar",                flag: "🇶🇦", dial: "+974", example: "3312 3456"   },
  { code: "KW", name: "Kuwait",               flag: "🇰🇼", dial: "+965", example: "5012 3456"   },
  { code: "BH", name: "Bahrain",              flag: "🇧🇭", dial: "+973", example: "3600 1234"   },
  { code: "OM", name: "Oman",                 flag: "🇴🇲", dial: "+968", example: "9212 3456"   },
  { code: "JO", name: "Jordan",               flag: "🇯🇴", dial: "+962", example: "7 9012 3456" },
  { code: "LB", name: "Lebanon",              flag: "🇱🇧", dial: "+961", example: "71 123 456"  },
  { code: "EG", name: "Egypt",                flag: "🇪🇬", dial: "+20",  example: "10 0123 4567" },
  { code: "IQ", name: "Iraq",                 flag: "🇮🇶", dial: "+964", example: "790 123 4567" },
  { code: "MA", name: "Morocco",              flag: "🇲🇦", dial: "+212", example: "6 12 34 56 78" },
  { code: "DZ", name: "Algeria",              flag: "🇩🇿", dial: "+213", example: "551 23 45 67" },
  { code: "TN", name: "Tunisia",              flag: "🇹🇳", dial: "+216", example: "20 123 456"  },
  { code: "LY", name: "Libya",                flag: "🇱🇾", dial: "+218", example: "91 123 4567" },
  { code: "SD", name: "Sudan",                flag: "🇸🇩", dial: "+249", example: "91 123 4567" },
  { code: "GB", name: "United Kingdom",       flag: "🇬🇧", dial: "+44",  example: "7700 900123" },
  { code: "US", name: "United States",        flag: "🇺🇸", dial: "+1",   example: "201 555 0123" },
  { code: "CA", name: "Canada",               flag: "🇨🇦", dial: "+1",   example: "204 555 0123" },
  { code: "AU", name: "Australia",            flag: "🇦🇺", dial: "+61",  example: "412 345 678" },
  { code: "DE", name: "Germany",              flag: "🇩🇪", dial: "+49",  example: "151 2345 6789" },
  { code: "FR", name: "France",               flag: "🇫🇷", dial: "+33",  example: "6 12 34 56 78" },
  { code: "IT", name: "Italy",                flag: "🇮🇹", dial: "+39",  example: "312 345 6789" },
  { code: "ES", name: "Spain",                flag: "🇪🇸", dial: "+34",  example: "612 345 678" },
  { code: "NL", name: "Netherlands",          flag: "🇳🇱", dial: "+31",  example: "6 12345678"  },
  { code: "TR", name: "Turkey",               flag: "🇹🇷", dial: "+90",  example: "501 234 5678" },
  { code: "PK", name: "Pakistan",             flag: "🇵🇰", dial: "+92",  example: "301 2345678" },
  { code: "IN", name: "India",                flag: "🇮🇳", dial: "+91",  example: "98765 43210" },
  { code: "SG", name: "Singapore",            flag: "🇸🇬", dial: "+65",  example: "8123 4567"   },
  { code: "MY", name: "Malaysia",             flag: "🇲🇾", dial: "+60",  example: "12 345 6789" },
  { code: "PH", name: "Philippines",          flag: "🇵🇭", dial: "+63",  example: "912 345 6789" },
  { code: "ZA", name: "South Africa",         flag: "🇿🇦", dial: "+27",  example: "71 123 4567" },
  { code: "NG", name: "Nigeria",              flag: "🇳🇬", dial: "+234", example: "802 123 4567" },
  { code: "KE", name: "Kenya",                flag: "🇰🇪", dial: "+254", example: "712 345 678" },
  { code: "GH", name: "Ghana",                flag: "🇬🇭", dial: "+233", example: "20 123 4567" },
];

/* ─── Request Custom Plan modal ─────────────────────────── */
function ContactSalesModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [name,    setName]    = useState("");
  const [email,   setEmail]   = useState("");
  const [company, setCompany] = useState("");
  const [country, setCountry] = useState("AE");
  const [phone,   setPhone]   = useState("");
  const [message, setMessage] = useState("");
  const [status,  setStatus]  = useState<"idle" | "sending" | "sent" | "error">("idle");

  const selectedCountry = COUNTRIES.find(c => c.code === country) ?? COUNTRIES[0];

  const handleCountryChange = (code: string) => {
    setCountry(code);
    setPhone("");
  };

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("sending");
    const fullPhone = phone ? `${selectedCountry.dial} ${phone}` : "";
    try {
      const res = await fetch("/api/contact-sales", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, company, country: selectedCountry.name, phone: fullPhone, message }),
      });
      if (res.ok) { setStatus("sent"); }
      else { setStatus("error"); }
    } catch { setStatus("error"); }
  };

  const reset = () => {
    setName(""); setEmail(""); setCompany(""); setCountry("AE"); setPhone(""); setMessage("");
    setStatus("idle"); onClose();
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" onClick={reset}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[92vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* header */}
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between shrink-0" style={{ background: "linear-gradient(135deg,#1a3a5c,#0d4a4a)" }}>
          <div>
            <div className="text-white font-bold text-lg">Request Custom Plan</div>
            <div className="text-slate-300 text-xs mt-0.5">Tell us about your company and requirements</div>
          </div>
          <button onClick={reset} className="text-slate-300 hover:text-white transition-colors cursor-pointer">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto">
          {status === "sent" ? (
            <div className="text-center py-8">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <div className="text-slate-900 font-semibold text-lg mb-1">Request received!</div>
              <div className="text-slate-500 text-sm">We'll be in touch within 1 business day to discuss your requirements.</div>
              <button onClick={reset} className="mt-5 px-6 py-2 rounded-lg text-sm font-medium text-white cursor-pointer" style={{ background: "#378ADD" }}>Done</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">

              {/* Row 1: name + email */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Full name *</label>
                  <input required value={name} onChange={e => setName(e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    placeholder="Ahmed Al-Rashid" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Work email *</label>
                  <input required type="email" value={email} onChange={e => setEmail(e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    placeholder="ahmed@company.com" />
                </div>
              </div>

              {/* Row 2: company */}
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Company / Group name</label>
                <input value={company} onChange={e => setCompany(e.target.value)}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="Al-Rashid Construction Group" />
              </div>

              {/* Row 3: country + phone */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Country *</label>
                  <select
                    required
                    value={country}
                    onChange={e => handleCountryChange(e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                  >
                    {COUNTRIES.map(c => (
                      <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Phone number</label>
                  <div className="flex rounded-lg border border-slate-200 overflow-hidden focus-within:ring-2 focus-within:ring-blue-400">
                    <span className="flex items-center px-3 bg-slate-50 border-r border-slate-200 text-sm text-slate-600 shrink-0 font-mono whitespace-nowrap">
                      {selectedCountry.flag} {selectedCountry.dial}
                    </span>
                    <input
                      type="tel"
                      value={phone}
                      onChange={e => setPhone(e.target.value.replace(/[^0-9\s\-]/g, ""))}
                      className="flex-1 min-w-0 px-3 py-2 text-sm focus:outline-none bg-white"
                      placeholder={selectedCountry.example}
                    />
                  </div>
                </div>
              </div>

              {/* Row 4: requirements */}
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Tell us about your requirements</label>
                <textarea value={message} onChange={e => setMessage(e.target.value)} rows={3}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="How many companies, projects, users? Any specific modules or integrations you need?" />
              </div>

              {status === "error" && (
                <div className="text-red-500 text-xs">Something went wrong — please email us at sales@finproject.io</div>
              )}

              <button
                type="submit"
                disabled={status === "sending"}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-white transition-all cursor-pointer disabled:opacity-60"
                style={{ background: "#378ADD" }}
              >
                <Send className="w-4 h-4" />
                {status === "sending" ? "Sending…" : "Send enquiry"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
import { DemoSetupModal } from "@/components/DemoSetupModal";
import { PricingSection } from "@/components/PricingSection";
import { Footer } from "@/components/website/Footer";


const NAV = [
  { label: "Who It's For", href: "/#who-its-for" },
  { label: "Features", href: "/#features" },
  { label: "Pricing", href: "/#pricing" },
  { label: "How it works", href: "/#how-it-works" },
];

const FEATURES_LEADERSHIP = [
  { icon: BarChart3, title: "Cash Flow Forecasting & Analytics", desc: "Real-time cash position across every project. Forecast inflows and outflows, monitor burn rate, and track AR/AP balances — all consolidated in one board-ready view.", color: "bg-rose-500" },
  { icon: Building2, title: "Multi-Company Consolidation", desc: "Manage subsidiaries, JV entities, and group companies under one account — complete financial isolation, single login, no duplicate data entry.", color: "bg-teal-500" },
  { icon: TrendingUp, title: "Revenue Recognition & Reporting", desc: "Accurate earned revenue, advance liability tracking, and period-end reports your board and auditors can rely on — automatically generated from live project data.", color: "bg-indigo-500" },
  { icon: Globe, title: "Multi-Currency Consolidation", desc: "Native support for 20+ currencies — USD, EUR, GBP, AED, INR and more — with correct decimal rounding and cross-entity visibility.", color: "bg-emerald-500" },
];

const FEATURES_FINANCE_TEAM = [
  { icon: FileText, title: "Certified Valuations & Retention", desc: "Generate progress certificates, track retention withheld and released, and maintain a full audit trail per client — eliminating manual spreadsheet reconciliation.", color: "bg-blue-500" },
  { icon: Layers, title: "Subcontractor Claims & Back-Charges", desc: "Process SC payment applications, partial payments, back-charges, and retention releases — all in one view per project, per subcontractor.", color: "bg-violet-500" },
  { icon: ClipboardList, title: "BOQ & Budget vs Actuals", desc: "Import bills of quantities, link to cost codes, and compare budget vs actuals in real time — with variation orders tracked and approved inline.", color: "bg-amber-500" },
  { icon: Users, title: "Payroll & Project Expenses", desc: "Run multi-staff payroll with allowances and deductions, and log project-level expenses — all allocated to the right cost code automatically.", color: "bg-cyan-500" },
  { icon: Shield, title: "Role-Based Access Control", desc: "Admin, Project Manager, Finance, and Viewer roles — each with isolated data access so the right people see the right numbers.", color: "bg-orange-500" },
  { icon: DollarSign, title: "PDC & Promissory Note Tracking", desc: "Track post-dated cheques and promissory notes alongside your AR balance — no more spreadsheets to chase upcoming collections.", color: "bg-pink-500" },
];

const STATS = [
  { value: "150+", label: "SMEs Trust Us Worldwide" },
  { value: "600+", label: "Active Users" },
  { value: "20+", label: "Currencies Supported" },
  { value: "11", label: "Financial Modules" },
];

const HOW_IT_WORKS = [
  {
    step: "01",
    title: "Click Try Demo",
    desc: "No email, no sign-up. Enter your details, choose your plan, and get instant access to a fully-loaded demo workspace — pre-seeded with real construction data.",
    color: "from-blue-600 to-blue-700",
  },
  {
    step: "02",
    title: "Your workspace, your plan",
    desc: "Your demo is personalised with your company name, country, and selected plan. Switch plans anytime from the demo banner to see what features each tier unlocks.",
    color: "from-violet-600 to-violet-700",
  },
  {
    step: "03",
    title: "Explore every module",
    desc: "Browse client invoices, subcontractor payments, payroll, BOQ, cash flow, and more — all pre-loaded with realistic project data so you see exactly how it works.",
    color: "from-emerald-600 to-teal-600",
  },
];

const ROI_POINTS = [
  {
    icon: LineChart,
    headline: "Know your cash position across all projects in real time",
    detail: "No more waiting for end-of-month reports. Every invoice raised, every payment received, every SC claim processed — reflected instantly in your consolidated cash view.",
    color: "text-emerald-600",
    bg: "bg-emerald-50",
  },
  {
    icon: RefreshCw,
    headline: "Zero spreadsheets for retention tracking",
    detail: "Retention withheld and due-for-release is tracked automatically per client and per project. Get alerts when retention is contractually due — never leave money on the table again.",
    color: "text-blue-600",
    bg: "bg-blue-50",
  },
  {
    icon: Clock,
    headline: "Payroll & expenses in minutes, not hours",
    detail: "Run project-level payroll with allowances, deductions, and cost-code allocation in a few clicks. Expenses are logged against the right project the moment they're submitted.",
    color: "text-violet-600",
    bg: "bg-violet-50",
  },
  {
    icon: Target,
    headline: "Board-ready reports — no manual assembly",
    detail: "Revenue recognition, AR aging, AP summary, budget vs actuals — generated from live data and formatted for finance leadership, not just operations teams.",
    color: "text-amber-600",
    bg: "bg-amber-50",
  },
];

const PAIN_POINTS = [
  { icon: AlertTriangle, text: "Chasing retention releases months after practical completion — because it's stuck in a spreadsheet nobody checks", color: "text-amber-600" },
  { icon: RefreshCw, text: "Reconciling subcontractor claims against back-charges at month-end, across five different tabs", color: "text-rose-600" },
  { icon: Clock, text: "Month-end scramble: manually consolidating project P&Ls from site managers the night before the board meeting", color: "text-violet-600" },
  { icon: Coins, text: "Forecasting cash position without knowing what SC payments are due next week — or what clients owe you right now", color: "text-blue-600" },
  { icon: Globe, text: "Running projects in multiple currencies with no system that handles rounding, VAT, and FX exposure in one place", color: "text-teal-600" },
  { icon: BadgeCheck, text: "No audit trail for payment certificates — finance approves, but there's no record of what was certified, when, or why", color: "text-indigo-600" },
];


function PortalMockup() {
  return (
    <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
      <div className="bg-slate-800 px-4 py-2.5 flex items-center gap-2">
        <div className="w-2.5 h-2.5 rounded-full bg-rose-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
        <span className="ml-3 text-[10px] text-slate-400 font-mono">finproject.app/portal</span>
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-sm font-bold text-slate-800">Financial Dashboard</div>
            <div className="text-[10px] text-slate-400">3 active · 5 total projects</div>
          </div>
          <div className="flex gap-1">
            {["Dashboard", "Projects", "Masters", "Team"].map((t, i) => (
              <div key={t} className={`px-2.5 py-1 rounded text-[9px] font-semibold ${i === 0 ? 'bg-slate-800 text-white' : 'text-slate-400'}`}>{t}</div>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-4 gap-2.5 mb-4">
          {[
            { label: "Total Contract Value", val: "USD 48,250,000", Icon: Briefcase, trend: "" },
            { label: "AR Balance", val: "USD 8,750,000", Icon: Wallet, trend: "text-rose-500" },
            { label: "AP Balance", val: "USD 4,320,000", Icon: Wallet, trend: "text-rose-500" },
            { label: "Net Cash", val: "USD 12,100,000", Icon: TrendingUp, trend: "text-emerald-600" },
          ].map((kpi) => (
            <div key={kpi.label} className="bg-slate-50 rounded-lg p-2.5 border border-slate-100">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[8px] uppercase tracking-wider text-slate-400 font-medium">{kpi.label}</span>
                <kpi.Icon className="w-3 h-3 text-slate-300" />
              </div>
              <div className={`text-[11px] font-bold ${kpi.trend || 'text-slate-800'}`}>{kpi.val}</div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          <div className="bg-slate-50 rounded-lg p-2.5 border border-slate-100">
            <div className="text-[10px] font-bold text-slate-700 mb-2">AR Aging Analysis</div>
            {[
              { label: "Current (< 30d)", pct: 45, color: "bg-emerald-500" },
              { label: "30–60 days", pct: 25, color: "bg-amber-400" },
              { label: "60–90 days", pct: 18, color: "bg-orange-500" },
              { label: "90+ days", pct: 12, color: "bg-rose-500" },
            ].map(a => (
              <div key={a.label} className="mb-1.5">
                <div className="flex justify-between text-[8px] text-slate-400 mb-0.5"><span>{a.label}</span></div>
                <div className="h-1 bg-slate-200 rounded-full"><div className={`h-full ${a.color} rounded-full`} style={{ width: `${a.pct}%` }} /></div>
              </div>
            ))}
          </div>
          <div className="bg-slate-50 rounded-lg p-2.5 border border-slate-100">
            <div className="text-[10px] font-bold text-slate-700 mb-2">AP & Payables Summary</div>
            {[
              { label: "SC Outstanding", val: "USD 2,180,000", color: "text-rose-500" },
              { label: "Supplier Outstanding", val: "USD 1,440,000", color: "text-rose-500" },
              { label: "Total AP Balance", val: "USD 4,320,000", color: "text-rose-600", bold: true },
              { label: "Total Client Invoiced", val: "USD 31,400,000", color: "text-slate-700" },
            ].map(r => (
              <div key={r.label} className="flex justify-between py-0.5 text-[8px]">
                <span className={`text-slate-400 ${r.bold ? 'font-bold text-slate-600' : ''}`}>{r.label}</span>
                <span className={`font-semibold ${r.color}`}>{r.val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function AppPreviewMockup({ activeTab }: { activeTab: "projects" | "dashboard" | "masters" }) {
  return (
    <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
      <div className="bg-slate-800 px-4 py-2.5 flex items-center gap-2">
        <div className="w-2.5 h-2.5 rounded-full bg-rose-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
        <span className="ml-3 text-[10px] text-slate-400 font-mono">
          {activeTab === "projects" ? "finproject.app/portal/projects" : activeTab === "dashboard" ? "finproject.app/project/manhattan-tower" : "finproject.app/portal/masters"}
        </span>
      </div>

      {activeTab === "projects" && (
        <div className="p-4">
          <div className="grid grid-cols-4 gap-2.5 mb-4">
            {[
              { label: "Total Contract Value", val: "USD 94,600,000", Icon: Briefcase, color: "text-slate-800" },
              { label: "Total Invoiced", val: "USD 52,140,000", Icon: FileText, color: "text-slate-800" },
              { label: "Total Collected", val: "USD 43,870,000", Icon: Wallet, color: "text-emerald-600" },
              { label: "Active Projects", val: "4", Icon: FolderOpen, color: "text-blue-600" },
            ].map((kpi) => (
              <div key={kpi.label} className="bg-slate-50 rounded-lg p-2.5 border border-slate-100">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[8px] uppercase tracking-wider text-slate-400 font-medium">{kpi.label}</span>
                  <kpi.Icon className="w-3 h-3 text-slate-300" />
                </div>
                <div className={`text-[11px] font-bold ${kpi.color}`}>{kpi.val}</div>
              </div>
            ))}
          </div>
          <div className="space-y-2">
            {[
              { name: "Manhattan Office Tower", status: "Active", statusColor: "bg-emerald-100 text-emerald-700", contract: "USD 28,500,000", invoiced: "USD 17,200,000", pct: 60 },
              { name: "Sydney Harbor Infrastructure", status: "Active", statusColor: "bg-emerald-100 text-emerald-700", contract: "USD 34,100,000", invoiced: "USD 19,940,000", pct: 58 },
              { name: "London Crossrail Extension", status: "Active", statusColor: "bg-emerald-100 text-emerald-700", contract: "USD 22,000,000", invoiced: "USD 11,000,000", pct: 50 },
              { name: "Singapore Port Expansion", status: "Completed", statusColor: "bg-slate-100 text-slate-500", contract: "USD 10,000,000", invoiced: "USD 10,000,000", pct: 100 },
            ].map((proj) => (
              <div key={proj.name} className="bg-slate-50 rounded-lg p-2.5 border border-slate-100">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] font-bold text-slate-700">{proj.name}</span>
                    <span className={`text-[7px] font-semibold px-1.5 py-0.5 rounded-full ${proj.statusColor}`}>{proj.status}</span>
                  </div>
                  <div className="flex gap-3 text-[8px]">
                    <span className="text-slate-400">Contract: <span className="text-slate-600 font-semibold">{proj.contract}</span></span>
                    <span className="text-slate-400">Invoiced: <span className="text-blue-600 font-semibold">{proj.invoiced}</span></span>
                  </div>
                </div>
                <div className="h-1 bg-slate-200 rounded-full">
                  <div className="h-full bg-blue-500 rounded-full" style={{ width: `${proj.pct}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === "dashboard" && (
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-sm font-bold text-slate-800">Manhattan Office Tower</div>
              <div className="text-[10px] text-slate-400">Contract Value: USD 28,500,000</div>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-2.5 mb-4">
            {[
              { label: "Contract Value", val: "USD 28,500,000", Icon: Briefcase, color: "text-slate-800" },
              { label: "Total Invoiced", val: "USD 17,200,000", Icon: FileText, color: "text-slate-800" },
              { label: "Earned Revenue", val: "USD 15,960,000", Icon: TrendingUp, color: "text-emerald-600" },
              { label: "Net Cash Position", val: "USD 10,480,000", Icon: DollarSign, color: "text-emerald-600" },
            ].map((kpi) => (
              <div key={kpi.label} className="bg-slate-50 rounded-lg p-2.5 border border-slate-100">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[8px] uppercase tracking-wider text-slate-400 font-medium">{kpi.label}</span>
                  <kpi.Icon className="w-3 h-3 text-slate-300" />
                </div>
                <div className={`text-[11px] font-bold ${kpi.color}`}>{kpi.val}</div>
              </div>
            ))}
          </div>
          <div className="bg-slate-50 rounded-lg p-2.5 border border-slate-100">
            <div className="text-[10px] font-bold text-slate-700 mb-2">Revenue Recognition Breakdown</div>
            <div className="grid grid-cols-2 gap-x-6">
              {[
                { label: "Gross Invoiced", val: "USD 17,200,000", color: "text-slate-700", bold: false },
                { label: "Advance Invoiced", val: "USD 2,850,000", color: "text-orange-600", bold: false, indent: true },
                { label: "Progress Billing", val: "USD 14,350,000", color: "text-blue-600", bold: false, indent: true },
                { label: "Retention Release", val: "USD 610,000", color: "text-teal-600", bold: false, indent: true },
                { label: "Less Retention Held", val: "−USD 1,000,000", color: "text-amber-600", bold: false, indent: true },
                { label: "Earned Revenue Net", val: "USD 15,960,000", color: "text-emerald-700", bold: true },
                { label: "Advance Received Liability", val: "USD 2,850,000", color: "text-orange-600", bold: false },
                { label: "Advance Outstanding", val: "USD 1,200,000", color: "text-slate-600", bold: false },
                { label: "Outstanding AR Balance", val: "USD 4,870,000", color: "text-rose-600", bold: true },
              ].map((r) => (
                <div key={r.label} className={`flex justify-between py-0.5 text-[8px] ${r.bold ? 'border-t border-slate-200 pt-1 mt-0.5' : ''}`}>
                  <span className={`text-slate-400 ${r.indent ? 'pl-2' : ''} ${r.bold ? 'font-bold text-slate-600' : ''}`}>{r.label}</span>
                  <span className={`font-semibold ${r.color}`}>{r.val}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === "masters" && (
        <div className="p-4">
          <div className="flex items-center gap-1 mb-3">
            {["Clients", "Subcontractors", "Suppliers", "Staff / Payroll", "Expense Categories"].map((tab, i) => (
              <div key={tab} className={`px-2.5 py-1 rounded text-[9px] font-semibold ${i === 0 ? 'bg-slate-800 text-white' : 'text-slate-400'}`}>{tab}</div>
            ))}
          </div>
          <div className="bg-slate-50 rounded-lg border border-slate-100 overflow-hidden">
            <div className="grid grid-cols-6 gap-0 bg-slate-100 border-b border-slate-200 px-3 py-1.5">
              {["ID", "Client Name", "Type", "Contact Person", "Country", "Status"].map((h) => (
                <div key={h} className="text-[8px] font-bold text-slate-500 uppercase tracking-wider">{h}</div>
              ))}
            </div>
            {[
              { id: "C-001", name: "Apex Development Corp.", type: "Corporate", contact: "James Thornton", country: "United States", status: "Active" },
              { id: "C-002", name: "Pacific Infrastructure Ltd.", type: "Corporate", contact: "Sarah Okafor", country: "Australia", status: "Active" },
              { id: "C-003", name: "Meridian Capital Group", type: "Corporate", contact: "Liam Chen", country: "United Kingdom", status: "Inactive" },
            ].map((row, i) => (
              <div key={row.id} className={`grid grid-cols-6 gap-0 px-3 py-2 text-[8px] border-b border-slate-100 last:border-0 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                <div className="text-slate-400 font-mono">{row.id}</div>
                <div className="text-slate-700 font-semibold flex items-center gap-1"><Building2 className="w-2.5 h-2.5 text-blue-400" />{row.name}</div>
                <div className="text-slate-500">{row.type}</div>
                <div className="text-slate-500">{row.contact}</div>
                <div className="text-slate-500">{row.country}</div>
                <div>
                  <span className={`px-1.5 py-0.5 rounded-full text-[7px] font-semibold ${row.status === "Active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{row.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function ClientInvoicingMockup() {
  return (
    <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
      <div className="bg-slate-800 px-4 py-2.5 flex items-center gap-2">
        <div className="w-2.5 h-2.5 rounded-full bg-rose-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
        <span className="ml-3 text-[10px] text-slate-400 font-mono">finproject.app/project/marina-tower/client</span>
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-sm font-bold text-slate-800">Client Revenue & Invoicing</div>
            <div className="text-[10px] text-slate-400">Marina Tower — Phase 2</div>
          </div>
          <div className="flex gap-1">
            {["Invoices", "Quotations", "Receipts"].map((t, i) => (
              <div key={t} className={`px-2.5 py-1 rounded text-[9px] font-semibold ${i === 0 ? 'bg-blue-600 text-white' : 'text-slate-400 bg-slate-100'}`}>{t}</div>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2.5 mb-4">
          {[
            { label: "Earned Revenue", val: "USD 11,200,000", color: "text-emerald-600" },
            { label: "AR Balance", val: "USD 3,140,000", color: "text-rose-600" },
            { label: "Retention Held", val: "USD 617,000", color: "text-amber-600" },
          ].map(k => (
            <div key={k.label} className="bg-slate-50 rounded-lg p-2.5 border border-slate-100">
              <div className="text-[8px] uppercase tracking-wider text-slate-400 font-medium mb-1">{k.label}</div>
              <div className={`text-[11px] font-bold ${k.color}`}>{k.val}</div>
            </div>
          ))}
        </div>
        <div className="bg-slate-50 rounded-lg border border-slate-100 overflow-hidden">
          <div className="grid grid-cols-7 gap-0 bg-slate-100 border-b border-slate-200 px-3 py-1.5">
            {["Ref", "Date", "Description", "Gross", "Retention", "Net", "Status"].map(h => (
              <div key={h} className="text-[8px] font-bold text-slate-500 uppercase tracking-wider">{h}</div>
            ))}
          </div>
          {[
            { ref: "INV-007", date: "15 Mar 2026", desc: "IPC #7 — Progress Billing", gross: "USD 2,180,000", ret: "−USD 109,000", net: "USD 2,071,000", status: "Sent", statusColor: "bg-blue-100 text-blue-700" },
            { ref: "INV-006", date: "28 Feb 2026", desc: "IPC #6 — Structural Works", gross: "USD 1,950,000", ret: "−USD 97,500", net: "USD 1,852,500", status: "Paid", statusColor: "bg-emerald-100 text-emerald-700" },
            { ref: "INV-005", date: "31 Jan 2026", desc: "IPC #5 — Substructure", gross: "USD 2,400,000", ret: "−USD 120,000", net: "USD 2,280,000", status: "Paid", statusColor: "bg-emerald-100 text-emerald-700" },
            { ref: "INV-ADV", date: "01 Nov 2025", desc: "Advance Payment Invoice", gross: "USD 1,850,000", ret: "—", net: "USD 1,850,000", status: "Paid", statusColor: "bg-emerald-100 text-emerald-700" },
          ].map((row, i) => (
            <div key={row.ref} className={`grid grid-cols-7 gap-0 px-3 py-2 text-[8px] border-b border-slate-100 last:border-0 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
              <div className="text-blue-600 font-mono font-semibold">{row.ref}</div>
              <div className="text-slate-500">{row.date}</div>
              <div className="text-slate-700 font-medium">{row.desc}</div>
              <div className="text-slate-700 font-semibold">{row.gross}</div>
              <div className="text-amber-600 font-semibold">{row.ret}</div>
              <div className="text-emerald-700 font-semibold">{row.net}</div>
              <div><span className={`px-1.5 py-0.5 rounded-full text-[7px] font-semibold ${row.statusColor}`}>{row.status}</span></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SubcontractorMockup() {
  return (
    <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
      <div className="bg-slate-800 px-4 py-2.5 flex items-center gap-2">
        <div className="w-2.5 h-2.5 rounded-full bg-rose-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
        <span className="ml-3 text-[10px] text-slate-400 font-mono">finproject.app/project/marina-tower/subcontractors</span>
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-sm font-bold text-slate-800">Subcontractor Management</div>
            <div className="text-[10px] text-slate-400">Marina Tower — Phase 2 · 6 Active SCs</div>
          </div>
          <div className="flex gap-1">
            {["SC Invoices", "Payments", "PO / VO"].map((t, i) => (
              <div key={t} className={`px-2.5 py-1 rounded text-[9px] font-semibold ${i === 0 ? 'bg-violet-600 text-white' : 'text-slate-400 bg-slate-100'}`}>{t}</div>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-4 gap-2.5 mb-4">
          {[
            { label: "Total SC Value", val: "USD 7,800,000", color: "text-slate-800" },
            { label: "Certified to Date", val: "USD 5,420,000", color: "text-blue-600" },
            { label: "Paid to Date", val: "USD 4,180,000", color: "text-emerald-600" },
            { label: "Retention Held", val: "USD 271,000", color: "text-amber-600" },
          ].map(k => (
            <div key={k.label} className="bg-slate-50 rounded-lg p-2.5 border border-slate-100">
              <div className="text-[8px] uppercase tracking-wider text-slate-400 font-medium mb-1">{k.label}</div>
              <div className={`text-[11px] font-bold ${k.color}`}>{k.val}</div>
            </div>
          ))}
        </div>
        <div className="bg-slate-50 rounded-lg border border-slate-100 overflow-hidden">
          <div className="grid grid-cols-7 gap-0 bg-slate-100 border-b border-slate-200 px-3 py-1.5">
            {["SC Ref", "Subcontractor", "Trade", "PO Value", "Certified", "Outstanding", "Status"].map(h => (
              <div key={h} className="text-[8px] font-bold text-slate-500 uppercase tracking-wider">{h}</div>
            ))}
          </div>
          {[
            { ref: "SC-001", name: "Gulf Steel Fabricators", trade: "Structural Steel", po: "USD 3,200,000", cert: "USD 2,560,000", out: "USD 384,000", status: "Active", statusColor: "bg-emerald-100 text-emerald-700" },
            { ref: "SC-002", name: "Al Noor MEP Services", trade: "MEP / HVAC", po: "USD 2,100,000", cert: "USD 1,470,000", out: "USD 221,000", status: "Active", statusColor: "bg-emerald-100 text-emerald-700" },
            { ref: "SC-003", name: "Apex Concrete LLC", trade: "Concrete Works", po: "USD 1,500,000", cert: "USD 1,050,000", out: "USD 157,500", status: "Active", statusColor: "bg-emerald-100 text-emerald-700" },
            { ref: "SC-004", name: "Precision Facades Ltd", trade: "Facade / Cladding", po: "USD 1,000,000", cert: "USD 340,000", out: "USD 51,000", status: "Active", statusColor: "bg-emerald-100 text-emerald-700" },
          ].map((row, i) => (
            <div key={row.ref} className={`grid grid-cols-7 gap-0 px-3 py-2 text-[8px] border-b border-slate-100 last:border-0 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
              <div className="text-violet-600 font-mono font-semibold">{row.ref}</div>
              <div className="text-slate-700 font-medium">{row.name}</div>
              <div className="text-slate-500">{row.trade}</div>
              <div className="text-slate-700 font-semibold">{row.po}</div>
              <div className="text-blue-600 font-semibold">{row.cert}</div>
              <div className="text-rose-600 font-semibold">{row.out}</div>
              <div><span className={`px-1.5 py-0.5 rounded-full text-[7px] font-semibold ${row.statusColor}`}>{row.status}</span></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PayrollMockup() {
  return (
    <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
      <div className="bg-slate-800 px-4 py-2.5 flex items-center gap-2">
        <div className="w-2.5 h-2.5 rounded-full bg-rose-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
        <span className="ml-3 text-[10px] text-slate-400 font-mono">finproject.app/project/marina-tower/payroll</span>
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-sm font-bold text-slate-800">Project Payroll</div>
            <div className="text-[10px] text-slate-400">Marina Tower — Phase 2 · March 2026</div>
          </div>
          <div className="flex gap-1">
            <div className="px-2.5 py-1 rounded text-[9px] font-semibold bg-cyan-600 text-white">Payroll Entries</div>
            <div className="px-2.5 py-1 rounded text-[9px] font-semibold text-slate-400 bg-slate-100">Run Payroll</div>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-2.5 mb-4">
          {[
            { label: "Total Staff", val: "12", color: "text-slate-800" },
            { label: "Gross Payroll", val: "USD 74,500", color: "text-slate-800" },
            { label: "Deductions", val: "USD 8,940", color: "text-rose-600" },
            { label: "Net Payable", val: "USD 65,560", color: "text-emerald-600" },
          ].map(k => (
            <div key={k.label} className="bg-slate-50 rounded-lg p-2.5 border border-slate-100">
              <div className="text-[8px] uppercase tracking-wider text-slate-400 font-medium mb-1">{k.label}</div>
              <div className={`text-[11px] font-bold ${k.color}`}>{k.val}</div>
            </div>
          ))}
        </div>
        <div className="bg-slate-50 rounded-lg border border-slate-100 overflow-hidden">
          <div className="grid grid-cols-8 gap-0 bg-slate-100 border-b border-slate-200 px-3 py-1.5">
            {["ID", "Employee", "Role", "Basic", "Housing", "Transport", "Deductions", "Net Pay"].map(h => (
              <div key={h} className="text-[8px] font-bold text-slate-500 uppercase tracking-wider">{h}</div>
            ))}
          </div>
          {[
            { id: "E-001", name: "Ahmed Khalil", role: "Site Engineer", basic: "8,500", housing: "2,500", transport: "800", ded: "1,180", net: "10,620" },
            { id: "E-002", name: "Ravi Sharma", role: "QS / Estimator", basic: "7,200", housing: "2,000", transport: "600", ded: "980", net: "8,820" },
            { id: "E-003", name: "Fatima Hassan", role: "Project Accountant", basic: "9,000", housing: "3,000", transport: "800", ded: "1,280", net: "11,520" },
            { id: "E-004", name: "John Mbeki", role: "Foreman", basic: "5,500", housing: "1,500", transport: "500", ded: "750", net: "6,750" },
            { id: "E-005", name: "Priya Patel", role: "Admin / Coordinator", basic: "4,800", housing: "1,200", transport: "400", ded: "640", net: "5,760" },
          ].map((row, i) => (
            <div key={row.id} className={`grid grid-cols-8 gap-0 px-3 py-2 text-[8px] border-b border-slate-100 last:border-0 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
              <div className="text-slate-400 font-mono">{row.id}</div>
              <div className="text-slate-700 font-semibold">{row.name}</div>
              <div className="text-slate-500">{row.role}</div>
              <div className="text-slate-700">{row.basic}</div>
              <div className="text-slate-700">{row.housing}</div>
              <div className="text-slate-700">{row.transport}</div>
              <div className="text-rose-600 font-semibold">{row.ded}</div>
              <div className="text-emerald-700 font-bold">{row.net}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function TeamMockup() {
  return (
    <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
      <div className="bg-slate-800 px-4 py-2.5 flex items-center gap-2">
        <div className="w-2.5 h-2.5 rounded-full bg-rose-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
        <span className="ml-3 text-[10px] text-slate-400 font-mono">finproject.app/portal/team</span>
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-sm font-bold text-slate-800">Project Team & Access</div>
            <div className="text-[10px] text-slate-400">Gulf Construction Group · 3 companies · 14 members</div>
          </div>
          <div className="px-3 py-1.5 rounded-lg text-[9px] font-semibold bg-blue-600 text-white">+ Invite Member</div>
        </div>
        <div className="grid grid-cols-3 gap-2.5 mb-4">
          {[
            { label: "Total Members", val: "14", color: "text-slate-800" },
            { label: "Active Roles", val: "4", color: "text-blue-600" },
            { label: "Companies", val: "3", color: "text-teal-600" },
          ].map(k => (
            <div key={k.label} className="bg-slate-50 rounded-lg p-2.5 border border-slate-100">
              <div className="text-[8px] uppercase tracking-wider text-slate-400 font-medium mb-1">{k.label}</div>
              <div className={`text-[11px] font-bold ${k.color}`}>{k.val}</div>
            </div>
          ))}
        </div>
        <div className="bg-slate-50 rounded-lg border border-slate-100 overflow-hidden">
          <div className="grid grid-cols-5 gap-0 bg-slate-100 border-b border-slate-200 px-3 py-1.5">
            {["Member", "Role", "Company", "Projects", "Status"].map(h => (
              <div key={h} className="text-[8px] font-bold text-slate-500 uppercase tracking-wider">{h}</div>
            ))}
          </div>
          {[
            { name: "Tariq Al-Mansouri", role: "Admin", roleBg: "bg-rose-100 text-rose-700", company: "Gulf Construction HQ", projects: "All (12)", status: "Online" },
            { name: "Fatima Hassan", role: "Finance", roleBg: "bg-blue-100 text-blue-700", company: "Gulf Construction HQ", projects: "8 assigned", status: "Online" },
            { name: "Ahmed Khalil", role: "Project Manager", roleBg: "bg-amber-100 text-amber-700", company: "Gulf Marine LLC", projects: "3 assigned", status: "Online" },
            { name: "Sarah Williams", role: "Finance", roleBg: "bg-blue-100 text-blue-700", company: "Gulf Interiors Ltd", projects: "4 assigned", status: "Offline" },
            { name: "James Okonkwo", role: "Viewer", roleBg: "bg-slate-100 text-slate-600", company: "Gulf Construction HQ", projects: "2 assigned", status: "Offline" },
          ].map((row, i) => (
            <div key={row.name} className={`grid grid-cols-5 gap-0 px-3 py-2 text-[8px] border-b border-slate-100 last:border-0 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
              <div className="text-slate-700 font-semibold flex items-center gap-1.5">
                <div className={`w-4 h-4 rounded-full ${row.status === "Online" ? "bg-emerald-500" : "bg-slate-300"} flex items-center justify-center`}>
                  <span className="text-[5px] text-white font-bold">{row.name.split(" ").map(n => n[0]).join("")}</span>
                </div>
                {row.name}
              </div>
              <div><span className={`px-1.5 py-0.5 rounded-full text-[7px] font-semibold ${row.roleBg}`}>{row.role}</span></div>
              <div className="text-slate-500">{row.company}</div>
              <div className="text-slate-500">{row.projects}</div>
              <div className="flex items-center gap-1">
                <div className={`w-1.5 h-1.5 rounded-full ${row.status === "Online" ? "bg-emerald-500" : "bg-slate-300"}`} />
                <span className="text-slate-500">{row.status}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const HERO_SLIDES = [
  { label: "Financial Dashboard", component: "portal" },
  { label: "Client Invoicing", component: "invoicing" },
  { label: "Subcontractor Mgmt", component: "subcontractor" },
  { label: "Project Payroll", component: "payroll" },
  { label: "Team & Access", component: "team" },
] as const;

type SlideKey = typeof HERO_SLIDES[number]["component"];

function HeroSlider() {
  const [activeSlide, setActiveSlide] = useState<SlideKey>("portal");
  const [isPaused, setIsPaused] = useState(false);

  const nextSlide = useCallback(() => {
    setActiveSlide(prev => {
      const idx = HERO_SLIDES.findIndex(s => s.component === prev);
      return HERO_SLIDES[(idx + 1) % HERO_SLIDES.length].component;
    });
  }, []);

  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, [isPaused, nextSlide]);

  return (
    <div
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="flex justify-center mb-5">
        <div className="flex bg-slate-100 rounded-xl p-1 gap-1 flex-wrap justify-center">
          {HERO_SLIDES.map(s => (
            <button
              key={s.component}
              onClick={() => setActiveSlide(s.component)}
              className={`px-3 sm:px-4 py-2 rounded-lg text-[11px] sm:text-xs font-semibold transition-all cursor-pointer ${
                activeSlide === s.component
                  ? "bg-white text-slate-900 shadow-sm border border-slate-200"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>
      <div className="relative overflow-hidden rounded-2xl">
        <div className="transition-opacity duration-500 ease-in-out">
          {activeSlide === "portal" && <PortalMockup />}
          {activeSlide === "invoicing" && <ClientInvoicingMockup />}
          {activeSlide === "subcontractor" && <SubcontractorMockup />}
          {activeSlide === "payroll" && <PayrollMockup />}
          {activeSlide === "team" && <TeamMockup />}
        </div>
      </div>
      <div className="flex justify-center gap-1.5 mt-4">
        {HERO_SLIDES.map(s => (
          <button
            key={s.component}
            onClick={() => setActiveSlide(s.component)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              activeSlide === s.component ? "w-8 bg-slate-700" : "w-3 bg-slate-300 hover:bg-slate-400"
            }`}
            aria-label={s.label}
          />
        ))}
      </div>
    </div>
  );
}

export default function Home() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activePreviewTab, setActivePreviewTab] = useState<"projects" | "dashboard" | "masters">("projects");
  const [demoSetupOpen, setDemoSetupOpen] = useState(false);
  const [demoSetupPlanId, setDemoSetupPlanId] = useState<string>("pro");
  const [contactSalesOpen, setContactSalesOpen] = useState(false);

  const openDemo = () => { setDemoSetupPlanId("pro"); setDemoSetupOpen(true); };
  const openDemoForPlan = (planId: string) => { setDemoSetupPlanId(planId); setDemoSetupOpen(true); };

  return (
    <div className="min-h-screen font-sans bg-white text-slate-900">
      <DemoSetupModal key={demoSetupPlanId} open={demoSetupOpen} initialPlanId={demoSetupPlanId} onClose={() => setDemoSetupOpen(false)} />
      <ContactSalesModal open={contactSalesOpen} onClose={() => setContactSalesOpen(false)} />

      {/* ── Navbar ── */}
      <nav className="fixed top-0 inset-x-0 z-50 border-b border-white/10 shadow-lg" style={{ background: "linear-gradient(135deg, #0d4a4a 0%, #1a3a5c 60%, #1a2a5a 100%)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/images/finproject-logo.png" alt="FinProject" className="h-9 w-auto rounded-lg" style={{ border: "2px solid #ffffff", padding: "2px", background: "#ffffff" }} />
            <span className="font-bold text-lg text-white tracking-tight">FinProject</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            {NAV.map(n => (
              <a key={n.label} href={n.href} className="text-sm font-medium text-white/70 hover:text-white transition-colors">{n.label}</a>
            ))}
          </div>
          <div className="hidden md:flex items-center gap-3">
            <Link href="/app/login" className="text-sm font-medium text-white/70 hover:text-white transition-colors px-4 py-2">Sign In</Link>
            <a
              href="#pricing"
              className="text-sm font-medium text-white/70 hover:text-white transition-colors border border-white/30 hover:border-white/60 px-4 py-2 rounded-lg"
            >
              See Pricing
            </a>
            <button
              onClick={openDemo}
              className="text-sm font-semibold text-slate-900 px-5 py-2.5 rounded-lg hover:opacity-90 transition-opacity shadow-md flex items-center gap-1.5 cursor-pointer"
              style={{ background: "#C9A840" }}
            >
              <PlayCircle className="w-3.5 h-3.5" />
              Try Free Demo
            </button>
          </div>
          <button
            className="md:hidden p-2 text-white/70 hover:text-white transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-white/10 px-4 py-4 space-y-1" style={{ background: "#0f3333" }}>
            {NAV.map(n => (
              <a
                key={n.label}
                href={n.href}
                className="block text-sm font-medium text-white/70 hover:text-white py-2.5 border-b border-white/10"
                onClick={() => setMobileMenuOpen(false)}
              >
                {n.label}
              </a>
            ))}
            <Link href="/app/login" className="block text-sm font-medium text-white/70 hover:text-white py-2.5 border-b border-white/10">Sign In</Link>
            <div className="flex flex-col gap-2 mt-3">
              <a
                href="#pricing"
                onClick={() => setMobileMenuOpen(false)}
                className="block w-full text-center text-sm font-medium text-white border border-white/30 px-5 py-3 rounded-lg"
              >
                See Pricing
              </a>
              <button
                onClick={() => { setMobileMenuOpen(false); openDemo(); }}
                className="block w-full text-center text-sm font-semibold text-slate-900 px-5 py-3 rounded-lg cursor-pointer"
                style={{ background: "#C9A840" }}
              >
                Try Free Demo
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* ── Hero ── */}
      <section className="pt-28 pb-20 px-4 sm:px-6 relative overflow-hidden bg-gradient-to-b from-slate-50 to-white">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-50 rounded-full blur-3xl opacity-60 pointer-events-none -translate-y-1/2 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-violet-50 rounded-full blur-3xl opacity-50 pointer-events-none translate-y-1/2 -translate-x-1/3" />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-8 border border-amber-300/40" style={{ background: "rgba(201, 168, 64, 0.1)" }}>
            <Zap className="w-3.5 h-3.5" style={{ color: "#C9A840" }} />
            <span className="text-xs font-semibold" style={{ color: "#C9A840" }}>Built for CFOs & Project Accountants in Construction</span>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-slate-900 leading-[1.08] tracking-tight mb-6">
            Every project's money,<br />
            <span className="bg-clip-text text-transparent" style={{ backgroundImage: "linear-gradient(135deg, #0d4a4a 0%, #1a3a5c 100%)" }}>one place</span>
          </h1>
          <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto mb-10 leading-relaxed">
            FinProject gives construction finance teams complete control over project cash flow, client invoicing, subcontractor claims, retention, and payroll — in a single platform built for how project finance actually works.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={openDemo}
              className="flex items-center gap-2 text-white font-semibold px-8 py-4 rounded-xl hover:opacity-90 transition-opacity shadow-lg text-base cursor-pointer w-full sm:w-auto justify-center"
              style={{ background: "linear-gradient(135deg, #0d4a4a 0%, #1a3a5c 100%)" }}
            >
              <PlayCircle className="w-4 h-4" />
              Try Free Demo — No Credit Card
            </button>
            <a
              href="#pricing"
              className="flex items-center gap-2 text-slate-700 font-semibold px-8 py-4 rounded-xl border border-slate-300 hover:bg-slate-50 transition-colors text-base w-full sm:w-auto justify-center"
            >
              See Pricing <ChevronRight className="w-4 h-4" />
            </a>
          </div>
          <p className="text-sm text-slate-500 mt-6">No credit card · Instant access · Personalised demo workspace</p>
        </div>

        <div className="max-w-5xl mx-auto mt-16 relative px-0 sm:px-4">
          <HeroSlider />
        </div>
      </section>

      {/* ── Stats ── */}
      <section className="py-14 border-y border-slate-100 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((s, i) => (
            <div key={i} className="text-center">
              <div className="text-3xl sm:text-4xl font-bold text-slate-900 mb-1">{s.value}</div>
              <div className="text-sm text-slate-500">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Who It's For ── */}
      <section id="who-its-for" className="py-20 sm:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-6 border border-amber-300/40" style={{ background: "rgba(201, 168, 64, 0.1)" }}>
              <span className="text-xs font-semibold" style={{ color: "#C9A840" }}>Who It's For</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4">Built for both sides of construction finance</h2>
            <p className="text-lg sm:text-xl text-slate-500 max-w-2xl mx-auto">Whether you're overseeing the books or running them day-to-day, FinProject gives you exactly what you need.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {/* CFO Card */}
            <div className="rounded-2xl border-2 overflow-hidden" style={{ borderColor: "#0d4a4a" }}>
              <div className="px-8 py-5 flex items-center gap-3" style={{ background: "linear-gradient(135deg, #0d4a4a 0%, #1a3a5c 100%)" }}>
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-white font-bold text-lg">CFO & Finance Director</div>
                  <div className="text-white/60 text-sm">Leadership & reporting</div>
                </div>
              </div>
              <div className="p-8 bg-white">
                <p className="text-slate-500 text-sm mb-6 leading-relaxed">You need consolidated visibility across every project — not a stack of spreadsheets from site accountants. You need to know your cash position before the board asks.</p>
                <div className="space-y-4">
                  {[
                    { pain: "No consolidated cash view across projects", solution: "Real-time multi-project cash dashboard with AR, AP, and net position" },
                    { pain: "Month-end is a manual data-collection exercise", solution: "Board-ready reports generated automatically from live project data" },
                    { pain: "Retention and PDCs tracked in separate spreadsheets", solution: "Unified retention and post-dated cheque ledger with automated alerts" },
                    { pain: "Multi-entity and JV financials don't reconcile cleanly", solution: "Multi-company workspace with complete financial isolation per entity" },
                  ].map((item, i) => (
                    <div key={i} className="flex gap-3">
                      <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "#0d4a4a" }} />
                      <div>
                        <div className="text-[11px] text-slate-400 line-through mb-0.5">{item.pain}</div>
                        <div className="text-sm text-slate-700 font-medium">{item.solution}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Project Accountant Card */}
            <div className="rounded-2xl border-2 overflow-hidden" style={{ borderColor: "#1D9E75" }}>
              <div className="px-8 py-5 flex items-center gap-3" style={{ background: "linear-gradient(135deg, #1D9E75 0%, #0d6e54 100%)" }}>
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                  <ClipboardList className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-white font-bold text-lg">Project Accountant</div>
                  <div className="text-white/60 text-sm">Day-to-day finance operations</div>
                </div>
              </div>
              <div className="p-8 bg-white">
                <p className="text-slate-500 text-sm mb-6 leading-relaxed">You're managing invoices, SC applications, payroll runs, and BOQ variance — often across multiple projects at once. You need a system that actually keeps up.</p>
                <div className="space-y-4">
                  {[
                    { pain: "Certified valuations built manually in Excel", solution: "Progress invoicing with retention, advance recovery, and full audit trail" },
                    { pain: "SC claims processed across disconnected files", solution: "Subcontractor payment certificates, back-charges, and retention in one view" },
                    { pain: "Payroll run takes half a day every cycle", solution: "Multi-staff payroll with allowances, deductions, and project cost allocation" },
                    { pain: "BOQ variance only discovered at month-end", solution: "Real-time budget vs actuals with variation orders tracked and approved inline" },
                  ].map((item, i) => (
                    <div key={i} className="flex gap-3">
                      <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "#1D9E75" }} />
                      <div>
                        <div className="text-[11px] text-slate-400 line-through mb-0.5">{item.pain}</div>
                        <div className="text-sm text-slate-700 font-medium">{item.solution}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Sound Familiar? ── */}
      <section className="py-20 sm:py-24 px-4 sm:px-6 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4">Sound familiar?</h2>
            <p className="text-lg sm:text-xl text-slate-500 max-w-2xl mx-auto">If you work in construction finance, these are probably not hypotheticals.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {PAIN_POINTS.map((pp, i) => (
              <div key={i} className="flex gap-4 p-5 bg-white rounded-2xl border border-slate-200 shadow-sm">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-slate-50 border border-slate-100`}>
                  <pp.icon className={`w-4.5 h-4.5 ${pp.color}`} style={{ width: "1.1rem", height: "1.1rem" }} />
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">{pp.text}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <p className="text-slate-500 text-base mb-6">FinProject was built specifically to eliminate these problems — not just paper over them.</p>
            <button
              onClick={openDemo}
              className="inline-flex items-center gap-2 text-white font-semibold px-8 py-4 rounded-xl hover:opacity-90 transition-opacity shadow-lg text-base cursor-pointer"
              style={{ background: "linear-gradient(135deg, #0d4a4a 0%, #1a3a5c 100%)" }}
            >
              <PlayCircle className="w-4 h-4" />
              See it for yourself — free demo
            </button>
          </div>
        </div>
      </section>

      {/* ── Features — Leadership & Reporting ── */}
      <section id="features" className="py-20 sm:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4">Everything you need to manage project finances</h2>
            <p className="text-lg sm:text-xl text-slate-500 max-w-2xl mx-auto">Organised by what matters most to each role — not just listed alphabetically.</p>
          </div>

          {/* For Leadership & Reporting */}
          <div className="mb-14">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px flex-1 bg-slate-200" />
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest" style={{ background: "linear-gradient(135deg, #0d4a4a 0%, #1a3a5c 100%)", color: "#fff" }}>
                <TrendingUp className="w-3.5 h-3.5" />
                For Leadership & Reporting
              </div>
              <div className="h-px flex-1 bg-slate-200" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {FEATURES_LEADERSHIP.map((f, i) => (
                <div key={i} className="p-6 rounded-2xl border border-slate-200 bg-white hover:shadow-md hover:border-slate-300 transition-all duration-300 group">
                  <div className={`w-10 h-10 rounded-xl ${f.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <f.icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">{f.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* For Finance Team */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px flex-1 bg-slate-200" />
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest" style={{ background: "#1D9E75", color: "#fff" }}>
                <ClipboardList className="w-3.5 h-3.5" />
                For Your Finance Team
              </div>
              <div className="h-px flex-1 bg-slate-200" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {FEATURES_FINANCE_TEAM.map((f, i) => (
                <div key={i} className="p-6 rounded-2xl border border-slate-200 bg-white hover:shadow-md hover:border-slate-300 transition-all duration-300 group">
                  <div className={`w-10 h-10 rounded-xl ${f.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <f.icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">{f.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── ROI / Impact ── */}
      <section className="py-20 sm:py-24 px-4 sm:px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-6 border border-amber-300/40" style={{ background: "rgba(201, 168, 64, 0.1)" }}>
              <span className="text-xs font-semibold" style={{ color: "#C9A840" }}>The Impact</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4">What changes when you stop using spreadsheets</h2>
            <p className="text-lg sm:text-xl text-slate-500 max-w-2xl mx-auto">Real outcomes — not just feature descriptions.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            {ROI_POINTS.map((r, i) => (
              <div key={i} className="p-8 bg-white rounded-2xl border border-slate-200 shadow-sm">
                <div className={`w-12 h-12 rounded-2xl ${r.bg} flex items-center justify-center mb-5`}>
                  <r.icon className={`w-6 h-6 ${r.color}`} />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-3">{r.headline}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{r.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Market indicators ── */}
      <section className="py-14 px-4 sm:px-6 bg-white border-b border-slate-100">
        <div className="max-w-5xl mx-auto">
          <div className="bg-slate-50 rounded-2xl border border-slate-200 px-8 py-6">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-5 text-center">Designed for construction finance teams across</p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                "General Contractors", "MEP Contractors", "Civil & Infrastructure",
                "Fit-Out Companies", "JV & Consortium Groups", "Specialist Subcontractors",
                "MENA Markets", "UK & Europe", "Africa & SE Asia",
              ].map((tag, i) => (
                <span key={i} className="bg-white border border-slate-200 text-slate-600 text-sm font-medium px-4 py-2 rounded-full shadow-sm">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section id="how-it-works" className="py-20 sm:py-24 px-4 sm:px-6 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-6 border border-amber-300/40" style={{ background: "rgba(201, 168, 64, 0.1)" }}>
              <span className="text-xs font-semibold" style={{ color: "#C9A840" }}>Getting Started</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4">Up and running in minutes</h2>
            <p className="text-lg sm:text-xl text-slate-500 max-w-xl mx-auto">No lengthy onboarding. No consultants. Go from sign-up to your first invoice in the same day.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {HOW_IT_WORKS.map((step, i) => (
              <div key={i} className="relative">
                {i < HOW_IT_WORKS.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-full w-full h-px bg-gradient-to-r from-slate-300 to-transparent z-0 -translate-x-4" />
                )}
                <div className="relative z-10 text-center p-6 bg-white rounded-2xl border border-slate-100 shadow-sm">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center mx-auto mb-5 shadow-md`}>
                    <span className="text-lg font-bold text-white">{step.step}</span>
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-3">{step.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <button
              onClick={openDemo}
              className="inline-flex items-center gap-2 text-white font-semibold px-8 py-4 rounded-xl hover:opacity-90 transition-opacity shadow-lg text-base cursor-pointer"
              style={{ background: "linear-gradient(135deg, #0d4a4a 0%, #1a3a5c 100%)" }}
            >
              <PlayCircle className="w-4 h-4" />
              Start your free demo
            </button>
          </div>
        </div>
      </section>

      {/* ── App Preview ── */}
      <section id="project-app" className="py-20 sm:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-6 border border-amber-300/40" style={{ background: "rgba(201, 168, 64, 0.1)" }}>
              <span className="text-xs font-semibold" style={{ color: "#C9A840" }}>A closer look at the platform</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4">See it in action</h2>
            <p className="text-lg sm:text-xl text-slate-500 max-w-2xl mx-auto">Explore the actual interface — from the project list and financial dashboard to the company masters register.</p>
          </div>

          <div className="flex justify-center mb-8">
            <div className="flex bg-slate-100 rounded-xl p-1 gap-1">
              <button
                onClick={() => setActivePreviewTab("projects")}
                className={`flex items-center gap-2 px-5 sm:px-6 py-3 rounded-lg text-sm font-semibold transition-all cursor-pointer ${
                  activePreviewTab === "projects"
                    ? "bg-white text-slate-900 shadow-sm border border-slate-200"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <FolderOpen className="w-4 h-4" />
                Projects
              </button>
              <button
                onClick={() => setActivePreviewTab("dashboard")}
                className={`flex items-center gap-2 px-5 sm:px-6 py-3 rounded-lg text-sm font-semibold transition-all cursor-pointer ${
                  activePreviewTab === "dashboard"
                    ? "bg-white text-slate-900 shadow-sm border border-slate-200"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <LayoutDashboard className="w-4 h-4" />
                Project Dashboard
              </button>
              <button
                onClick={() => setActivePreviewTab("masters")}
                className={`flex items-center gap-2 px-5 sm:px-6 py-3 rounded-lg text-sm font-semibold transition-all cursor-pointer ${
                  activePreviewTab === "masters"
                    ? "bg-white text-slate-900 shadow-sm border border-slate-200"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <Users className="w-4 h-4" />
                Masters
              </button>
            </div>
          </div>

          <AppPreviewMockup activeTab={activePreviewTab} />
        </div>
      </section>

      {/* ── Pricing ── */}
      <PricingSection onTryDemo={openDemoForPlan} onContactSales={() => setContactSalesOpen(true)} />

      {/* ── Bottom Conversion Band ── */}
      <section className="py-20 sm:py-24 px-4 sm:px-6" style={{ background: "linear-gradient(135deg, #0d4a4a 0%, #1a3a5c 60%, #1a2a5a 100%)" }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">Stop managing project finances<br />in spreadsheets</h2>
          <p className="text-lg sm:text-xl text-white/60 mb-10 max-w-2xl mx-auto">FinProject gives your finance team one place for every invoice, every claim, every payroll run — and gives you the visibility to lead with confidence.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={openDemo}
              className="inline-flex items-center gap-2 font-bold px-8 sm:px-10 py-4 rounded-xl hover:opacity-90 transition-opacity text-base sm:text-lg shadow-2xl cursor-pointer w-full sm:w-auto justify-center text-slate-900"
              style={{ background: "#C9A840" }}
            >
              <PlayCircle className="w-5 h-5" />
              Try Free Demo
            </button>
            <a
              href="#pricing"
              className="inline-flex items-center gap-2 border-2 border-white/30 text-white font-semibold px-8 py-4 rounded-xl hover:border-white/60 transition-colors text-base w-full sm:w-auto justify-center"
            >
              See Pricing <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          <p className="text-sm text-white/40 mt-6">No credit card · Instant demo access · 7 days free</p>
        </div>
      </section>

      {/* ── Footer ── */}
      <Footer onTryDemo={openDemo} />
    </div>
  );
}
